<template>
  <v-stepper
    v-model="step"
    non-linear
    class="mb-3 br-10 font-paralucent"
    :class="{ 'dark-mode': isDark }"
    color="white"
  >
    <v-stepper-header ref="formHeader">
      <v-stepper-step step="1" editable data-testid="new-transaction-new-client-id-step">
        {{ i18n("Identificação") }}
      </v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step step="2" editable data-testid="new-transaction-new-client-doc-step">
        {{ i18n("Documentos") }}
      </v-stepper-step>
      <v-divider v-if="show_bank_accounts_tab"></v-divider>
      <v-stepper-step :step="accounts_step_number" v-if="show_bank_accounts_tab" :editable="step > 2 || operation == 'update'" data-testid="new-transaction-new-client-accounts-step">
        {{ i18n("Canais Bancários") }}
      </v-stepper-step>
      <v-divider v-if="show_approval_tab"></v-divider>
      <v-stepper-step :step="last_step_number" v-if="show_approval_tab && operation == 'create'" editable data-testid="new-transaction-new-client-create-step">
        {{ i18n("Criar/Aprovar") }}
      </v-stepper-step>
      <v-stepper-step :step="last_step_number" v-if="show_approval_tab && operation == 'update'" editable data-testid="new-transaction-new-client-update-step">
        {{ i18n("Atualizar/Aprovar") }}
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-content step="1">
      <v-container @click.prevent.stop="" class="cursor-type grid-list-sm">
        <v-layout class="row wrap align-center">
          <v-flex v-bind:class="{ xs12: !displayTotals, xs6: displayTotals }" pb-3 title-form display-1 p-relative>
            {{ i18n("Identificação") }}
            <v-snackbar v-model="display_feedback" :timeout="2000" bottom>ID copiado para a área de transferência</v-snackbar>
            <div class="caption no-input-focus title-form width-position ml-1" v-if="operation == 'update'" data-testid="subject-id-label">{{recordId}}
              <v-icon small @click="copyIdtoClipboard" data-testid="copy-subject-id-icon">content_copy</v-icon>
            </div>
          </v-flex>
          <v-flex xs12 sm6 mt-3 text-sm-right subtitle-1 style="color: var(--background-color)" v-if="displayTotals">
            {{i18n('Total Enviado')}}: <span v-for="r in filteredTransactionTotals">{{l(r.value, r.currency)}} / {{r.count}}<br /></span>
          </v-flex>
          <v-flex xs6 sm6 lg12 mt-3 text-sm-left subtitle-1 style="color: gray" v-if="this.record.blocked">
            <v-icon color="#FF0000">block</v-icon>Usuário bloqueado para transações.
          </v-flex>
          <v-flex class="xs6 lg2" :dark="isDark">
            <v-select
              box
              :label="i18n('Nacionalidade')"
              append-icon="public"
              v-model="record.document_type"
              :disabled="this.operation === 'update'"
              :clearable="this.operation === 'create'"
              @click:clear="record.document_type = null"
              :items="nationalitiesItems"
              data-testid="new-transaction-new-client-uf-select"
            ></v-select>
          </v-flex>
          <v-flex v-bind:class="{ lg3: !displayRfcPf, lg3: displayRfcPf }">
            <v-text-field
              box
              v-model="record.document_number"
              :errorMessages="errorMessagesFor('document_number')"
              :dark="isDark"
              :label="record.document_type.toUpperCase()"
              :mask="maskForDocumentNumber()"
              data-testid="new-transaction-new-client-document-number-input"
            ></v-text-field>
          </v-flex>
          <v-flex lg2 v-if="displayRfcPf">
            <v-text-field
              box
              v-model="record.rfc_pf"
              :errorMessages="errorMessagesFor('rfc_pf')"
              :dark="isDark"
              label="RFC (PF)"
              mask="AAAA-######-NNN"
              data-testid="new-transaction-new-client-rfc-pf-input"
            ></v-text-field>
          </v-flex>
          <v-flex v-bind:class="{ lg4: !displayRfcPf, lg3: displayRfcPf }">
            <v-text-field
              box
              v-model="record.social_name"
              :errorMessages="errorMessagesFor('social_name')"
              :dark="isDark"
              :label="i18n('Nome completo')"
              data-testid="new-transaction-new-client-social-name-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg2">
            <v-autocomplete
              v-model="record.fiscal_country"
              :errorMessages="errorMessagesFor('fiscal_country')"
              :items="countries"
              :dark="isDark"
              :label="i18n('País Domicílio Fiscal')"
              data-testid="new-transaction-new-client-fiscal-country-input"
            ></v-autocomplete>
          </v-flex>
          <v-flex class="xs12 lg2 pr-1 pl-2">
            <v-text-field
              box
              v-model="birthDateFormatted"
              :errorMessages="errorMessagesFor('creation_date')"
              :label="i18n('Data de nascimento')"
              :dark="isDark"
              mask="##/##/####"
              :return-masked-value="true"
              hint="DD/MM/YYYY"
              persistent-hint
              prepend-icon="event"
              data-testid="new-transaction-new-client-date-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg4">
            <v-currency-field
              v-bind="currency_config"
              box
              v-model="record.net_monthly_average_income"
              :errorMessages="errorMessagesFor('net_monthly_average_income')"
              :label="i18n('Faturamento médio mensal')"
              :dark="isDark"
              persistent-hint
              :hint="i18n('referente aos 12 meses anteriores')"
              data-testid="new-transaction-new-client-monthly-average-income-input"
            ></v-currency-field>
          </v-flex>
          <v-flex class="xs1 lg2">
            <v-select
              box
              v-model="record.phone_country_code"
              :errorMessages="errorMessagesFor('countryCode')"
              :dark="isDark"
              :label="i18n('Cod. País')"
              :items="getCountryCodes"
              data-testid="new-transaction-new-client-country-code-input"
              clearable
            ></v-select>
          </v-flex>
          <v-flex class="xs2 lg2">
            <v-text-field
              box
              v-model="record.phone_area_code"
              :errorMessages="errorMessagesFor('areaCode')"
              :dark="isDark"
              :label="i18n('Cod. Área')"
              data-testid="new-transaction-new-client-area-code-input"
            ></v-text-field>
          </v-flex>
            <v-flex class="xs12 lg2">
            <v-text-field
              box
              v-model="record.phone_number"
              :errorMessages="errorMessagesFor('phone_number')"
              :dark="isDark"
              :label="i18n('Telefone')"
              data-testid="new-transaction-new-client-phone-number-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-text-field
              box
              v-model="record.email"
              :errorMessages="errorMessagesFor('email')"
              :dark="isDark"
              label="Email"
              data-testid="new-transaction-new-client-email-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-text-field
              box
              v-model="record.website"
              :errorMessages="errorMessagesFor('website')"
              :dark="isDark"
              :label="i18n('Website')">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout class="align-center row wrap">
          <v-flex class="title-form md1 lg12">
            <div class="headline">{{ i18n("Endereço") }}</div>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-autocomplete
              v-model="record.address_country"
              :errorMessages="errorMessagesFor('address_country')"
              :items="countries"
              :dark="isDark"
              :label="i18n('País')"
              data-testid="new-transaction-new-client-country-input"
            ></v-autocomplete>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-text-field
              box
              v-model="record.address_postal_code"
              :errorMessages="errorMessagesFor('address_postal_code')"
              :dark="isDark"
              :mask="postalCodeMask"
              :label="i18n('CEP')"
              data-testid="new-transaction-new-client-postal-code-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-text-field
              box
              v-model="record.address_street"
              :errorMessages="errorMessagesFor('address_street')"
              :dark="isDark"
              :label="i18n('Logradouro')"
              data-testid="new-transaction-new-client-street-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs6 lg2">
            <v-text-field
              box
              v-model="record.address_number"
              :errorMessages="errorMessagesFor('address_number')"
              :dark="isDark"
              :label="i18n('Número')"
              data-testid="new-transaction-new-client-address-number-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs6 lg4">
            <v-text-field
              box
              v-model="record.address_neighborhood"
              :errorMessages="errorMessagesFor('address_neighborhood')"
              :dark="isDark"
              :label="i18n('Bairro')"
              data-testid="new-transaction-new-client-neighborhood-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg5">
            <v-text-field
              box
              v-model="record.address_complement"
              :errorMessages="errorMessagesFor('address_complement')"
              :dark="isDark"
              :label="i18n('Complemento')"
              data-testid="new-transaction-new-client-complement-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg4">
            <v-text-field
              box
              v-model="record.address_city"
              :errorMessages="errorMessagesFor('address_city')"
              :dark="isDark"
              :label="i18n('Cidade')"
              data-testid="new-transaction-new-client-city-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg3">
            <v-text-field
              box
              v-model="record.address_state"
              :errorMessages="errorMessagesFor('address_state')"
              :dark="isDark"
              :label="i18n('Estado')"
              data-testid="new-transaction-new-client-state-input"
            ></v-text-field>
          </v-flex>
          <v-flex class="xs12 lg12 justify-flex-end">
            <v-btn :dark="isDark" :disabled="step > 1 ? false : true" flat class="font-weight-black flex-1 secondary-btn-bg-color text-color-white" @click="$emit('cancelled')" data-testid="new-transaction-new-client-cancel-button">
              {{ i18n("Cancelar") }}
            </v-btn>
            <v-btn :dark="isDark" class="font-weight-black btn-bg-color flex-1 text-color-white" @click="skipAndScroll()" data-testid="new-transaction-new-client-continue-button">
              {{ i18n("Prosseguir") }}
            </v-btn>
          </v-flex>
          <document-select-modal
            v-if="show_document_type_form"
            :document_types="document_types"
            @select="selectDocumentType">
          </document-select-modal>
        </v-layout>
      </v-container>
    </v-stepper-content>
    <v-stepper-content step="2">
      <v-container class="grid-list-lg" @click="stopUnlessUploading">
        <v-layout class="row wrap">
          <v-flex class="xs12 lg12 title-form display-1">
            {{ i18n("Documentos") }}
          </v-flex>
          <v-flex class="xs12 lg12">
            <v-flex class="xs12 lg12">
            <Filetable
            :headers="headers"
            :items="record.documents"
            >
              <template v-slot:items="props">
                <td>{{ props.item.date || recordDate(props.item.inserted_at)}}</td>
                <td @click.prevent.stop="openOnNewWindow(props.item.file_url)">{{ props.item.filename || props.item.labels.join(',') }}</td>
              </template>
            </Filetable>
          </v-flex>
          </v-flex>
          <v-flex class="xs12 lg12 d-flex">
            <v-btn
              :dark="isDark"
              v-if="!loadingFile"
              outline
              data-ignore-toogle="true"
              @click.prevent="pickFile"
              class="upload-btn-bg-color"
              data-testid="new-transaction-new-client-send-file"
            >
              {{ i18n("Enviar arquivos") }}
            </v-btn>
            <v-btn :dark="isDark" v-if="loadingFile" class="secondary-btn-bg-color upload-btn-bg-color" outline>
              <v-progress-circular :indeterminate="loadingFile" :value="0" size="22" class="ml-2"></v-progress-circular>
              {{ i18n("Carregando arquivo... aguarde") }}
            </v-btn>
            <input
              name="person_file"
              style="display: none"
              type="file"
              multiple
              ref="person_upload_input"
              accept="*/*"
              @change="onFilePicked"
            />
          </v-flex>
        </v-layout>
        <v-flex class="xs12 lg12 justify-flex-end">
          <v-btn :dark="isDark" flat class="font-weight-black flex-1 secondary-btn-bg-color text-color-white" @click="step = 1" data-testid="new-transaction-new-client-rollback-button">{{ i18n("Voltar") }}</v-btn>
          <v-btn
            :dark="isDark"
            class="font-weight-black flex-1 btn-bg-color text-color-white"
            @click="step = 3"
            data-testid="new-transaction-new-client-continue-button"
            v-if="(admin || operation == 'update') && !partnerPortal"
          >
            {{ i18n("Prosseguir") }}
          </v-btn>
          <v-btn
            :dark="isDark"
            class="font-weight-black flex-1 btn-bg-color text-color-white"
            @click="updatePerson"
            data-testid="new-transaction-new-client-update-button"
            v-if="!admin && (operation == 'update' && !subjectId)"
          >
            {{ i18n("Atualizar") }}
          </v-btn>
          <v-btn
            :dark="isDark"
            class="font-weight-black flex-1 btn-bg-color text-color-white"
            @click="createPerson"
            data-testid="new-transaction-new-client-create-button"
            v-if="!admin && operation == 'create'"
          >
            {{ i18n("Cadastrar") }}
          </v-btn>
        </v-flex>
      </v-container>
    </v-stepper-content>

    <v-stepper-content :step="accounts_step_number">
      <v-container @click.prevent.stop="">
        <v-flex class="xs12 lg12 title-form display-1">
          {{ i18n("Canais Bancários") }}
        </v-flex>
        <BankAccounts :subject_id="subjectId" wait_for_subject :show_partner_facilita_bank_accounts="false" :show_external_bank_accounts="true" :show_admin_external_bank_account="show_admin_external_bank_account" :subject_document_type="this.record.document_type" />

        <v-flex class="xs12 lg12 justify-flex-end">
          <v-btn :dark="isDark" flat class="font-weight-black flex-1 secondary-btn-bg-color text-color-white" @click="step = 2" data-testid="new-transaction-new-client-rollback-button" v-if="operation == 'update'">
            {{ i18n("Voltar") }}
          </v-btn>
          <v-btn :dark="isDark" class="flex-2 btn-bg-color text-color-white" @click="step = 1" data-testid="new-transaction-new-client-create-button" v-if="operation == 'create'">
            {{ i18n('Novo Cadastro') }}
          </v-btn>
          <v-btn :dark="isDark" class="font-weight-black flex-1 btn-bg-color text-color-white" @click="step = 4" data-testid="new-transaction-new-client-continue-button" v-if="admin">
            {{ i18n("Prosseguir") }}
          </v-btn>
          <v-btn :dark="isDark" class="font-weight-black flex-1 btn-bg-color text-color-white" @click="updatePerson" data-testid="new-transaction-new-client-update-button" v-if="operation == 'update' && !admin">
            {{ i18n("Atualizar") }}
          </v-btn>
        </v-flex>
      </v-container>
    </v-stepper-content>

    <v-stepper-content :step="last_step_number" v-if="show_approval_tab">
      <v-container class="grid-list-lg" @click.prevent.stop="">
        <v-layout class="row wrap" v-if="show_reprove_form == false">
          <v-flex class="xs12 sm6 display-1 title-form" v-if="operation == 'create'">
            {{ i18n("Cadastrar/Aprovar") }}
          </v-flex>
          <v-flex class="xs12 sm6 display-1 title-form" v-if="operation == 'update'">
            {{ i18n("Atualizar/Aprovar") }}
          </v-flex>
          <v-flex xs12 sm6 mt-3 text-sm-right subtitle-1 style="color: var(--background-color)">
            Total movimentado: {{l(record.transaction_balance, customer_currency)}}
          </v-flex>
          <v-flex class="xs12 lg12">
            <v-radio-group v-model="record.clearance_level" class="input-control-with-grow">
              <div slot="label">{{ i18n("Autorização") }}*</div>
              <div class="display-flex-baseline-start">
                <v-radio :label="i18n('Não autorizado')" value="0"></v-radio>
                <v-radio :label="i18n('Aprovado Nivel 1')" value="1"></v-radio>
                <v-radio :label="i18n('Aprovado Nivel 2')" value="2"></v-radio>
                <v-flex v-if="record.clearance_level > 1">
                  <v-checkbox
                    append-icon="vertical_align_top"
                    v-model="enableTransactionLimit"
                    hide-details
                    label="Definir limite de total transacionado">
                  </v-checkbox>
                  <v-currency-field
                    :label="customer_currency"
                    v-if="enableTransactionLimit"
                    v-bind="currency_config"
                    v-model="record.transaction_limit">
                  </v-currency-field>
                </v-flex>
              </div>
            </v-radio-group>
          </v-flex>
          <v-flex class="xs12 lg12 justify-flex-end">
            <v-btn :dark="isDark" flat class="font-weight-black flex-2 secondary-btn-bg-color text-color-white" @click="step = last_step_number - 1">Voltar</v-btn>
            <v-btn
              :dark="isDark"
              class="font-weight-black btn-bg-color flex-3"
              @click="createPerson"
              v-if="operation == 'create'"
            >
              {{ i18n("Cadastrar") }}
            </v-btn>
            <v-btn
              :dark="isDark"
              class="font-weight-black btn-bg-color flex-3 text-color-white"
              @click="updatePerson"
              v-if="operation == 'update'"
            >
              {{ i18n("Atualizar") }}
            </v-btn>
            <v-btn
              class="font-weight-black btn-bg-red flex-1"
              :dark="isDark"
              @click="showRejectReasonForm"
              v-if="operation == 'update'"
            >
              {{ i18n("Reprovar") }}
            </v-btn>
          </v-flex>
          <v-flex class="xs12 lg12 justify-flex-end hidden-sm-and-up" v-if="operation == 'update'">
            <v-btn :dark="isDark" class="font-weight-black flex-1 btn-bg-red" @click="showRejectReasonForm">
              {{ i18n("Reprovar") }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-layout class="row wrap" v-if="show_reprove_form == true">
          <v-flex class="xs12 lg12 display-1 title-form">
            {{ i18n("Reprovar Cadastro") }}
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-checkbox
              v-model="rejected_items"
              :label="i18n('Documento rejeitado')"
              value="photo_id_rejected"
              :disabled="disabledWhen('photo_id_missing')"
            ></v-checkbox>
            <v-checkbox
              v-model="rejected_items"
              :label="i18n('Documento não enviado')"
              value="photo_id_missing"
              :disabled="disabledWhen('photo_id_rejected')"
            ></v-checkbox>
            <v-checkbox v-model="rejected_items" :label="i18n('Declaração de IR não enviada')" value="tax_declaration_required"></v-checkbox>
            <v-checkbox v-model="rejected_items" :label="i18n('Declaração de IR insuficiente')" value="tax_declaration_insufficient"></v-checkbox>
            <v-checkbox v-model="rejected_items" :label="i18n('Declaração de IR desatualizada')" value="old_tax_declaration"></v-checkbox>
          </v-flex>
          <v-flex class="xs12 lg6">
            <v-checkbox
              v-model="rejected_items"
              :label="i18n('Comprovante de residência rejeitado')"
              value="proof_of_address_rejected"
              :disabled="disabledWhen('proof_of_address_missing')"
            ></v-checkbox>
            <v-checkbox
              v-model="rejected_items"
              :label="i18n('Comprovante de residência não enviado')"
              value="proof_of_address_missing"
              :disabled="disabledWhen('proof_of_address_rejected')"
            ></v-checkbox>
            <v-checkbox v-model="rejected_items" :label="i18n('Endereço não informado')" value="missing_address_info"></v-checkbox>
          </v-flex>
          <v-flex class="xs12 lg12">
            <v-textarea
              box
              name="reprove_reason"
              :label="i18n('Motivo Reprovação') + '*'"
              v-model="reprove_reason"
            ></v-textarea>
          </v-flex>
          <v-flex class="xs12 lg12 justify-flex-end">
            <v-btn :dark="isDark" flat class="font-weight-black flex-2 secondary-btn-bg-color text-color-white" @click="hideRejectReasonForm">Voltar</v-btn>
            <v-btn
              :dark="isDark"
              class="font-weight-black flex-1 btn-bg-red hidden-xs-only"
              @click="reprovePerson"
              v-if="operation == 'update'"
            >
              {{ i18n("Reprovar") }}
            </v-btn>
          </v-flex>
          <v-flex class="xs12 lg12 hidden-sm-and-up" v-if="operation == 'update'">
            <v-btn :dark="isDark" class="font-weight-black flex-1 btn-bg-red" @click="reprovePerson">
              {{ i18n("Reprovar") }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-stepper-content>
  </v-stepper>
</template>

<script>

import utils from "./shared/Utils"
import countries from "./shared/Countries"
import activities from "./shared/CompanyActivities"
import Filetable from "./components/DefaultDataTable"
import BankAccounts from "./components/BankAccounts"
import CountryPhoneCodes from "./shared/CountryPhoneCodes"
import DocumentTypeSelectModal from "./components/DocumentTypeSelectModal"

export default {
  name: "AdminPersonForm",
  components: {
    BankAccounts,
    Filetable,
    'document-select-modal': DocumentTypeSelectModal
  },
  props: {
    record: {
      type: Object
    },
    partnerPortal: {
      type: Boolean,
      default: false
    },
    admin: {
      type: Boolean,
      default: false
    },
    subject: {
      type: Boolean,
      default: false
    },
    operation: {
      type: String
    },
    isDark: {
      type: Boolean,
      default: false
    }
  },
  data(vm) {
    return {
      step: 1,
      activities,
      base_url: "",
      countries,
      currency_config: {
        decimal: ",",
        thousands: ".",
      },
      document_types: [],
      errors: {},
      loadingFile: false,
      recentlyCreatedId: "",
      display_feedback: false,
      enableTransactionLimit: null,
      birthDateFormatted: vm.formatDate(vm.record.birth_date),
      max_file_size: 20000000,
      show_reprove_form: false,
      show_document_type_form: false,
      transactionTotals: [],
      reprove_reason: '',
      rejected_items: [],
      baseNationalitiesItems: [
        {value: 'cpf', text: this.i18n('Brasileira')},
        {value: 'curp', text: this.i18n('Mexicana')},
        {value: 'cc', text: this.i18n('Colombiana')},
        {value: 'rut', text: this.i18n('Chilena')},
        {value: 'passport', text: this.i18n('Outra')},
      ],
      nationalitiesItems: [],
      headers: [
        { text: this.i18n('Data'), align: 'left', value: 'inserted_at', sortable: true },
        { text: this.i18n('File name'), value: 'inserted_at' ,align: 'left', sortable: false }
      ]
    }
  },
  beforeMount() {
    this.record.net_monthly_average_income = this.record.net_monthly_average_income || ""
    this.record.transaction_limit = this.record.transaction_limit || ""
    this.enableTransactionLimit = this.record.transaction_limit > 0
  },
  mounted() {
    if (this.admin) {
      this.base_url = this.$root.urls.admin_companies
      this.loadTransactionTotals()
    } else if (this.subject) {
      this.base_url = this.$root.urls.customer__people
    } else {
      this.base_url = this.$root.urls.companies
    }
    if (this.operation == "create") {
      this.record.fiscal_country = 'Brasil'
      this.nationalitiesItems = [...this.baseNationalitiesItems]
    } else {
      this.nationalitiesItems = [{value: this.record.document_type, text: this.customer_nationality}]
    }
    this.record.clearance_level = this.record.clearance_level + ""

    if (this.record.documents) {
      this.record.documents.forEach((file, index)=>{
        if (!file.file_url) {
          this.createFileUrl(file, index)
        }
      })
    }
  },
  computed: {
    recordId() {
      return String(this.record.id)
    },
    subjectId() {
      return this.subject && (this.record.id || this.recentlyCreatedId) || ''
    },
    postalCode() {
      return this.record.address_postal_code
    },
    postalCodeMask() {
      if (this.record.address_country == "Brasil") {
        return "##.###-###"
      } else {
        return ""
      }
    },
    filteredTransactionTotals() {
      return this.filterTransactionTotals(this.transactionTotals)
    },
    displayTotals() {
      return this.admin && this.operation != 'create'
    },
    show_bank_accounts_tab() {
      return this.subject
    },
    accounts_step_number() {
      return 3
    },
    show_approval_tab() {
      return this.admin && this.subject
    },
    last_step_number() {
      return this.show_bank_accounts_tab ? this.accounts_step_number + 1 : this.accounts_step_number
    },
    customer_currency() {
      switch (this.record.document_type) {
        case 'cpf':
          return 'BRL'
        case 'curp':
          return 'MXN'
        case 'cc':
        case 'ce':
        case 'nit':
          return 'COP'
        case 'rut':
          return 'CLP'
        default:
          return 'BRL'
      }
    },
    customer_nationality() {
      switch (this.record.document_type) {
        case 'cpf':
          return this.i18n('Brasileira')
        case 'curp':
          return this.i18n('Mexicana')
        case 'cc':
        case 'ce':
        case 'nit':
          return this.i18n('Colombiana')
        case 'rut':
          return this.i18n('Chilena')
        case 'passport':
          return this.i18n('Outra')
        default:
          return ''
      }
    },
    displayRfcPf() {
      return this.record.document_type == 'curp'
    },
    show_admin_external_bank_account() {
      return this.admin
    },
    getCountryCodes() {
      return CountryPhoneCodes
    }
  },
  watch: {
    "record.birth_date"(new_val) {
      this.birthDateFormatted = this.formatDate(new_val)
    },
    "record.document_type"(new_val){
      if (new_val == 'cc') {
        this.show_document_type_form = true
        this.document_types = ['cc', 'ce']
      } else if (new_val !== 'ce') {
        this.nationalitiesItems = [...this.baseNationalitiesItems]
      }
    },
    birthDateFormatted(new_val) {
      this.record.birth_date = this.parseDate(new_val)
    },
    postalCode(new_postal_code, old_postal_code) {
      if (new_postal_code != old_postal_code) {
        let postal_code = new_postal_code.replace(".", "").replace("-", "")

        if (this.record.address_country == "Brasil" && postal_code.length == 8) {
          let successCallback = (response) => {
            this.$set(this.record, "address_street", response.body.logradouro)
            this.$set(this.record, "address_city", response.body.cidade)
            this.$set(this.record, "address_state", response.body.estado)
          }

          let errorCallback = (response) => {}

          this.$http
            .get(`${this.$root.urls.cep_api}${postal_code}`)
            .then(successCallback, errorCallback)
        }
      }
    }
  },
  methods:
  {
    i18n(str) {
      return this.$root.l(str)
    },
    recordDate(date) {
      return utils.dbDateTimeToHuman(date).toString()
    },
    disabledWhen(value) {
      return this.rejected_items.includes(value)
    },
    parseDate(human_format_date) {
      return utils.humanDateToDb(human_format_date)
    },
    formatDate(db_format_date) {
      return utils.dbDateToHuman(db_format_date)
    },
    maskForDocumentNumber() {
      return utils.maskForDocumentType(this.record.document_type.toLowerCase())
    },
    copyIdtoClipboard() {
      navigator.clipboard.writeText(this.recordId).then(() => {
        this.display_feedback = true
      })
    },
    errorMessagesFor(field) {
      if (this.errors[field]) {
        return this.errors[field]
      } else {
        return []
      }
    },
    stopUnlessUploading(e) {
      if (e.target.type === "file") {
        return true
      } else {
        e.preventDefault()
        return false
      }
    },
    createFileUrl(file, index){
      let successCallback = (response) => {
        let { file_data } = response.body.data
        let { file_type } = file
        this.record.documents[index].file_url =  utils.b64ToUrl(file_data, file_type)
      }
      let errorCallback = (response) => {
        console.log(response)
      }
      let token = this.$cookies.get("api_token")
      this.$http.get(`${this.$root.urls.customer__subject}${this.record.id}/documents/${file.id}`
      ,{ headers: { Authorization: `Bearer ${token}` }}).then(successCallback, errorCallback)
    },
    pickFile(e) {
      this.$refs.person_upload_input.click()
      e.stopPropagation()
    },
    openOnNewWindow(url) {
      window.open(url, "_blank")
    },
     onFilePicked(e) {
        const batch_size = 20
        const files = e.target.files;

        if (files.length > 0 && files.length <= batch_size) {
          this.loadingFile = true;
          let filesProcessed = 0;
          let totalFiles = files.length;

          Array.from(files).forEach((file) => {
            let filename = file.name;

            if (filename.lastIndexOf(".") <= 0) {
            alert(this.i18n("Arquivo") + ' "' + filename + '" ' + this.i18n("é inválido"));
              filesProcessed++;
              if (filesProcessed === totalFiles) {
                this.loadingFile = false;
              }
              return;
            }

            const fr = new FileReader();
            fr.readAsDataURL(file);

            fr.addEventListener("load", () => {
              this.$nextTick(() => {
                let ext = filename.split(".").pop();
                let labels = [filename];
                let data = fr.result.match(/^[^,]*,(.*)$/)[1];
                let file_type = fr.result.match(/^data:([^;]*);.*$/)[1];
                let date = this.recordDate(Date.now());
                let size = data.length;

                if (size > this.max_file_size) {
                  alert(
                      ' "' +
                      filename +
                      '": ' +
                      this.i18n("Arquivos maiores que 14.9Mb não podem ser enviados")
                  );
                  filesProcessed++;
                  if (filesProcessed === totalFiles) {
                    this.loadingFile = false;
                  }
                  return;
                }

                const payload = {
                  file: {
                    name: filename,
                    ext,
                    data,
                  },
                };

                let uploadSuccess = (response) => {
                  if (response.body.data) {
                    let file_url =
                      this.$root.urls.public_uploads +
                        response.body.data.id +
                        "." +
                        response.body.data.ext;
                    this.record.documents = this.record.documents || [];
                    this.record.documents.push({
                      filename,
                      size,
                      labels,
                      file_url,
                      file_type,
                      date,
                    });
                  }
                  filesProcessed++;
                  if (filesProcessed === totalFiles) {
                    this.loadingFile = false;
                  }
                };

                let uploadError = (response) => {
                  alert(
                    this.i18n("Não foi possível completar o upload do arquivo") +
                      ' "' +
                      filename +
                      '"'
                  );
                  filesProcessed++;
                  if (filesProcessed === totalFiles) {
                    this.loadingFile = false;
                  }
                };

                this.uploadFile(payload, uploadSuccess, uploadError);
              });
            });
          });
        } else {
          alert(this.i18n('Selecione no máximo') + ' ' + batch_size + ' ' + this.i18n('arquivos'));
          this.fileName = "";
          this.fileUrl = "";
        }
      },
    uploadFile(payload, successCallback, errorCallback) {
      const token = this.$cookies.get("api_token")
      this.$http
        .post(this.$root.urls.upload, payload, { headers: { Authorization: `Bearer ${token}` } })
        .then(successCallback, errorCallback)
    },
    filterTransactionTotals(collection) {
      let filtered = collection.filter( (r) => { return r.count > 0 })

      if (filtered.length === 0) {
        return [{ currency: 'BRL', value: '0.00', count: 0 }]
      } else {
        return filtered.map((r) => { return {currency: r.currency, value: r.value, count: r.count} } )
      }
    },
    sumTransactionTotals(collection, currency, attribute) {
      return collection
        .filter( (r) => { return r.currency === currency })
        .reduce( (total, r) => { return total + parseFloat(r[attribute]) }, 0)
    },
    loadTransactionTotals() {
      let successCallback = (response) => {
        if (response.body.data) {
          this.transactionTotals = response.body.data
        }
      }

      let errorCallback = (response) => {
        console.log(response)
      }

      const token = this.$cookies.get('api_token')
      this.$http.get(
        `${this.$root.urls.admin_companies}${this.record.id}/transaction_values`,
        { headers: { Authorization: `Bearer ${token}` }}
      ).then(successCallback, errorCallback)
    },
    selectDocumentType(type) {
      this.record.document_type = type
      this.show_document_type_form = false
      if (type == 'ce') {
        this.nationalitiesItems = [...this.baseNationalitiesItems]

        this.nationalitiesItems = this.nationalitiesItems.filter(item => item.value !== 'cc')
        this.nationalitiesItems.push({value: type, text: this.i18n('Colombiana')})
      }
    },
    updatePerson(e) {
      if (!this.enableTransactionLimit || this.record.clearance_level < 2) this.record.transaction_limit = ''
      this.record = utils.normalizeUpdateRecordIfRequired(this.record)
      const payload = { person: this.record }

      let successCallback = (response) => {
        this.record = utils.letShowEmptyCurpIfRequired(this.record)
        this.$emit("onUpdate", this.record)
      }

      let errorCallback = (response) => {
        if (response.body.errors) {
          let errors = response.body.errors

          for (let field in errors) {
            errors[field] = errors[field].map((error_msg) => {
              return utils.translateErrorMessage(error_msg, this.$root.lang)
            })
          }

          this.errors = errors
          this.step = 1
        }
      }

      this.errors = {}
      const token = this.$cookies.get("api_token")
      const company_id = this.record.id
      this.$http
        .put(this.base_url + company_id, payload, { headers: { Authorization: `Bearer ${token}` } })
        .then(successCallback, errorCallback)
    },
    createPerson(e) {
      const payload = { person: this.record }

      let successCallback = (response) => {
        if (response.body.data) {
          this.errors = {}
          this.recentlyCreatedId = response.body.data.id
          this.step = 3
          this.$emit("onCreate", response.body.data)
        } else {
          console.log(response)
        }
      }

      let errorCallback = (response) => {
        if (response.body.errors) {
          let errors = response.body.errors

          for (let field in errors) {
            errors[field] = errors[field].map((error_msg) => {
              return utils.translateErrorMessage(error_msg, this.$root.lang)
            })
          }

          this.errors = errors
          this.step = 1
        }
      }

      this.errors = {}
      const token = this.$cookies.get("api_token")
      this.$http
        .post(this.base_url, payload, { headers: { Authorization: `Bearer ${token}` } })
        .then(successCallback, errorCallback)
    },
    showRejectReasonForm(e) {
      this.show_reprove_form = true
    },
    hideRejectReasonForm(e) {
      this.show_reprove_form = false
    },
    reprovePerson(e) {
      const payload = { reason: this.reprove_reason, items: this.rejected_items }

      let successCallback = (response) => {
        this.$emit("onReprove")
      }

      let errorCallback = (response) => {
        console.log("error")
        console.log(response)
      }

      const token = this.$cookies.get("api_token")
      const url = `${this.$root.urls.admin_companies}${this.record.id}/reprove`
      this.$http
        .post(url, payload, { headers: { Authorization: `Bearer ${token}` } })
        .then(successCallback, errorCallback)
    },
    l(number_value, currency) {
      return utils.currencyToLocal(number_value, currency)
    },
    skipAndScroll() {
      this.step = 2;
      this.$refs.formHeader.scrollIntoView();
    },
  },
  metaInfo: {
    title: "Facilita Pay"
  }
}
</script>

<style scoped>
.dark-mode {
  background: var(--background-color) !important;
}
.btn-style,
.btn-style--secondary {
  height: 56px;
}
.btn-bg-red {
  background-color: var(--light-red-color) !important;
  color: white !important;
}
.btn-style {
  background-color: var(--primary-button-background-color) !important;
}
.btn-style--secondary {
  background-color: var(--secondary-button-background-color) !important;
}
.dark-mode >>> .v-stepper__step span.primary {
  background-color: var(--secondary-button-background-color) !important;
}
.v-stepper__step >>> span.primary {
  background-color: var(--background-color) !important;
}
.dark-mode >>> .v-stepper__step .v-stepper__label {
  color: var(--primary-light-font-color) !important;
}
li.documentList {
  color: #333;
  cursor: pointer;
}
.title-form {
  color: #44517B !important;
}
.dark-mode .title-form {
  color: var(--secondary-button-background-color) !important;
}
.text-color-white,
.dark-mode .text-color-white{
  color: white !important;
}

.br-10 {
  border-radius: 10px;
}

.font-paralucent{
  font-family: paralucent !important;
}
.cursor-type {
  cursor: default;
}

.align-center {
  align-items: center;
}

.justify-flex-end {
  display: flex;
  justify-items: flex-end;
}

.p-relative {
  position: relative;
}

.width-position {
  width: 270px;
  position: absolute;
  top: 30px;
  left: 0px;
}

.flex-align {
  display: flex;
  align-items: baseline;
}

.flex-1 {
  flex: 1;
}

.flex-2 {
  flex: 2;
}
.flex-3 {
  flex: 3;
}
.btn-bg-color {
  background-color: var(--secondary-button-background-color) !important;
}

.flex-space-between {
  display: flex;
  justify-content: space-between;
}
.secondary-btn-bg-color {
  color: var(--background-color) !important;
}

.display-flex-baseline {
  display: flex;
  align-items: baseline;
}

.display-flex-baseline-start{
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  flex-wrap: wrap;
}
.text-color-gray {
  color: gray !important;
}

.dark-mode .upload-btn-bg-color{
  color: white !important;
}

.upload-btn-bg-color{
  color: var(--background-color) !important;
}
</style>



// WEBPACK FOOTER //
// src/AdminPersonForm.vue